<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// CRM-01h fase 3: a confirmação do "mover em bloco". As regras da etapa de destino ficam num
// interruptor só, desligado, que só administrador liga (decisões do Gate 1). A lista diz o que ele
// dispara: "resolver" em bloco manda pesquisa de satisfação, automações e webhooks para cada card.
// O servidor também recusa as regras de quem não é administrador; a tela não é a trava.
const props = defineProps({
  canApplyRules: { type: Boolean, default: false },
  // Teto por operação (KanbanBulkMove::MAX_CARDS no servidor).
  maxCards: { type: Number, default: 2000 },
});

const emit = defineEmits(['confirm']);

const { t } = useI18n();
const teams = useMapGetter('teams/getTeams');

const dialogRef = ref(null);
const stage = ref(null);
const count = ref(0);
const applyRules = ref(false);

// Os mesmos nomes de status da lista de conversas (e do diálogo da etapa).
const NOME_DO_STATUS = {
  open: () => t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
  pending: () => t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
  snoozed: () => t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
  resolved: () => t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
};

const efeitos = computed(() => {
  const regras = stage.value?.on_enter || {};
  const lista = [];
  if (regras.conversation_status) {
    const status =
      NOME_DO_STATUS[regras.conversation_status]?.() ||
      regras.conversation_status;
    lista.push(
      t(
        'KANBAN.BULK.EFFECT_STATUS',
        { count: count.value, status },
        count.value
      )
    );
  }
  if (regras.add_labels?.length) {
    lista.push(
      t('KANBAN.BULK.EFFECT_LABELS', { labels: regras.add_labels.join(', ') })
    );
  }
  if (regras.assign_team_id) {
    const id = Number(regras.assign_team_id);
    const nome = (teams.value || []).find(team => team.id === id)?.name;
    lista.push(t('KANBAN.BULK.EFFECT_TEAM', { team: nome || `#${id}` }));
  }
  return lista;
});

const acimaDoTeto = computed(() => count.value > props.maxCards);

const titulo = computed(() =>
  stage.value
    ? t(
        'KANBAN.BULK.CONFIRM_TITLE',
        { count: count.value, stage: stage.value.name },
        count.value
      )
    : ''
);

const open = ({ stage: destino, count: quantidade }) => {
  stage.value = destino;
  count.value = quantidade;
  applyRules.value = false;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const confirmar = () => {
  emit('confirm', {
    stage: stage.value,
    applyRules: props.canApplyRules && applyRules.value,
  });
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="titulo"
    :confirm-button-label="t('KANBAN.BULK.CONFIRM')"
    :disable-confirm-button="acimaDoTeto"
    @confirm="confirmar"
  >
    <div class="flex flex-col gap-3 text-sm">
      <p
        v-if="acimaDoTeto"
        class="px-3 py-2 rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ t('KANBAN.BULK.TOO_MANY', { count, limit: maxCards }) }}
      </p>
      <template v-else-if="efeitos.length">
        <div
          class="flex items-center justify-between gap-3"
          :class="{ 'opacity-60': !canApplyRules }"
        >
          <span class="text-n-slate-12">{{
            t('KANBAN.BULK.APPLY_RULES')
          }}</span>
          <span :class="{ 'pointer-events-none': !canApplyRules }">
            <Switch v-model="applyRules" />
          </span>
        </div>
        <p v-if="!canApplyRules" class="text-xs text-n-slate-11">
          {{ t('KANBAN.BULK.RULES_ADMIN_ONLY') }}
        </p>
        <div
          class="px-3 py-2 rounded-lg"
          :class="
            applyRules
              ? 'bg-n-amber-3 text-n-amber-11'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          <p class="font-medium">
            {{
              applyRules
                ? t('KANBAN.BULK.EFFECTS_ON')
                : t('KANBAN.BULK.EFFECTS_OFF')
            }}
          </p>
          <ul class="mt-1 list-disc ps-4">
            <li v-for="efeito in efeitos" :key="efeito">{{ efeito }}</li>
          </ul>
        </div>
      </template>
      <p v-else class="text-n-slate-11">{{ t('KANBAN.BULK.NO_RULES') }}</p>
    </div>
  </Dialog>
</template>
